import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const SettingsMenu = React.memo(({
  isSettingsOpen,
  onSetIsSettingsOpen,
  calibrationMode,
  onStartCalibration,
  onClearCalibration,
  showQuantities,
  onToggleShowQuantities,
  showInventory,
  onToggleShowInventory,
  onFileUpload,
  fileInputRef,
  selectedFileName,
  isCustomCsvActive,
  onClearCustomCsv,
  csvUploadError,
  onStartBinLightsDemo,
  // Additional props for full functionality
  setSelectedFileName,
  setCsvUploadError,
  setIsCustomCsvActive,
  setCustomCsvData,
  setShowBinGridPreview,
  getApiBaseUrl
}) => {
  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedFileName(file.name);
      setCsvUploadError(null);
      setIsCustomCsvActive(false);
      setCustomCsvData(null);
      if (file.type === 'text/csv' || file.name.endsWith('.csv')) {
        const reader = new FileReader();
        reader.onload = (e) => {
          const csvContent = e.target.result;
          if (!csvContent || csvContent.trim() === '') {
            setCsvUploadError('CSV file is empty or contains only whitespace.');
            setSelectedFileName('');
            if (fileInputRef.current) fileInputRef.current.value = '';
            return;
          }
          const lines = csvContent.split(/\r?\n/);
          const parsedGrid = [];
          let maxCols = 0;
          for (const line of lines) {
            const cells = line.split(',').map((cell) => cell.trim());
            parsedGrid.push(cells);
            if (cells.length > maxCols) maxCols = cells.length;
          }
          const rectangularGrid = parsedGrid.map((row) => {
            const newRow = [...row];
            while (newRow.length < maxCols) newRow.push('');
            return newRow.slice(0, maxCols);
          });
          while (rectangularGrid.length && rectangularGrid[rectangularGrid.length - 1].every((c) => c === '')) {
            rectangularGrid.pop();
          }
          if (rectangularGrid.length) {
            setCustomCsvData(rectangularGrid);
            setIsCustomCsvActive(true);
            setShowBinGridPreview(true);
            setCsvUploadError(null);
          } else {
            setCsvUploadError('Failed to parse CSV. File might be empty or malformed.');
          }
        };
        reader.onerror = () => {
          setCsvUploadError('Error reading the file.');
        };
        reader.readAsText(file);
      } else {
        setCsvUploadError('Invalid file type. Please upload a .csv file.');
      }
    }
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleClearCustomCsvAndBackend = async () => {
    setCustomCsvData(null);
    setIsCustomCsvActive(false);
    setCsvUploadError(null);
    setSelectedFileName('');
    setShowBinGridPreview(false);
    try {
      const apiUrl = `${getApiBaseUrl()}/api/clear-active-data`;
      await fetch(apiUrl, { method: 'POST' });
    } catch (error) {
      console.error('Error clearing active custom data on backend:', error);
    }
  };

  return (
    <AnimatePresence mode="wait">
      {isSettingsOpen && (
        <motion.div
          key="settings-menu"
          initial={{ y: '100%', opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: '100%', opacity: 0 }}
          transition={{ type: 'tween', ease: 'easeOut', duration: 0.35 }}
          className="fixed bottom-0 right-0 w-80 bg-white shadow-lg z-40 rounded-t-2xl overflow-hidden"
        >
          <div className="p-6">
            <h2 className="text-2xl font-bold mb-6 text-gray-800">Settings</h2>
            <div className="space-y-4">
              <button
                onClick={onStartCalibration}
                className="w-full px-4 py-2 rounded-lg text-white font-semibold bg-orange-500 hover:bg-orange-600 transition duration-150"
              >
                {calibrationMode === 'grid' ? 'Adjust Grid Corners' : 'Start Calibration'}
              </button>
              <button
                onClick={onClearCalibration}
                className="w-full px-4 py-2 rounded-lg text-white font-semibold bg-red-500 hover:bg-red-600 transition duration-150"
              >
                Clear Calibration
              </button>
              <button
                onClick={onToggleShowQuantities}
                className="w-full px-4 py-2 rounded-lg text-white font-semibold bg-purple-600 hover:bg-purple-700 transition duration-150"
              >
                {showQuantities ? 'Hide Quantities' : 'Show Quantities'}
              </button>
              <button
                onClick={onToggleShowInventory}
                className="w-full px-4 py-2 rounded-lg text-white font-semibold bg-teal-600 hover:bg-teal-700 transition duration-150"
              >
                {showInventory ? 'Hide Inventory' : 'Show Inventory'}
              </button>
              <button 
                onClick={onStartBinLightsDemo} 
                className="w-full px-4 py-2 rounded-lg text-white font-semibold transition duration-150 shadow-lg"
                style={{ 
                  background: 'linear-gradient(to right, #7c3aed, #ec4899)', 
                  border: 'none',
                  color: 'white'
                }}
                onMouseEnter={(e) => e.target.style.background = 'linear-gradient(to right, #6d28d9, #db2777)'}
                onMouseLeave={(e) => e.target.style.background = 'linear-gradient(to right, #7c3aed, #ec4899)'}
              >
                🎉 Bin Lights Demo
              </button>

              {/* CSV Upload Section */}
              <div className="mt-4 pt-4 border-t border-gray-300">
                <h3 className="text-lg font-semibold mb-2 text-gray-700">Custom Part Data</h3>
                <input
                  type="file"
                  accept=".csv"
                  onChange={handleFileUpload}
                  ref={fileInputRef}
                  className="hidden"
                  id="csvFileInput"
                />
                <button
                  onClick={() => fileInputRef.current && fileInputRef.current.click()}
                  className="w-full mb-2 px-4 py-2 rounded-lg text-white font-semibold bg-indigo-600 hover:bg-indigo-700 transition duration-150"
                >
                  Upload Custom CSV
                </button>
                {selectedFileName && (
                  <div className="text-sm text-gray-600 mb-2">
                    Loaded: <span className="font-medium">{selectedFileName}</span>
                  </div>
                )}
                {isCustomCsvActive && (
                  <button
                    onClick={handleClearCustomCsvAndBackend}
                    className="w-full px-4 py-2 rounded-lg text-white font-semibold bg-pink-500 hover:bg-pink-600 transition duration-150"
                  >
                    Clear Custom CSV (Use Default)
                  </button>
                )}
                {csvUploadError && (
                  <p className="text-red-500 text-xs mt-2">{csvUploadError}</p>
                )}
              </div>
            </div>
            <button
              onClick={() => onSetIsSettingsOpen(false)}
              className="mt-6 w-full px-4 py-2 rounded-lg text-gray-600 font-semibold bg-gray-200 hover:bg-gray-300 transition duration-150"
            >
              Close Settings
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}, (prevProps, nextProps) => {
  const keysToCompare = [
    'isSettingsOpen',
    'showQuantities', 
    'showInventory',
    'calibrationMode',
    'selectedFileName',
    'isCustomCsvActive',
    'csvUploadError',
    'onStartBinLightsDemo'
  ];
  
  return keysToCompare.every(key => prevProps[key] === nextProps[key]);
});

export default SettingsMenu; 